import { safeJson } from "src/app/helpers/Index";
import { faChairOffice, faLocationDot, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { SeasonService, VenueService } from "src/app/services/Index";
import "./AdminVenues.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	saving: boolean;
	venues: any[];
	seasons: any[];
	selectedVenueId: number | null;
	charts: any[];
	selectedChartId: number | null;
	areas: any[];
	dialog: null | "venue" | "chart" | "area" | "block" | "seats" | "editSeats";
	edit: any;
	/** The block whose seats are open, and what is in it. */
	seatRows: any[];
	selectedSeats: number[];
	seatsBusy: boolean;
};

/** Row labels A…Z, then AA…, for the seat generator's default. */
const rowLabels = (count: number) => {
	const out: string[] = [];
	for (let i = 0; i < count; i++) {
		let n = i, label = "";
		do { label = String.fromCharCode(65 + (n % 26)) + label; n = Math.floor(n / 26) - 1; } while (n >= 0);
		out.push(label);
	}
	return out;
};

/**
 * Venues and seating.
 *
 * Three columns, left to right: venue, then its chart for a season, then the
 * areas in that chart. It's a hierarchy and pretending otherwise with tabs
 * would hide where you are in it.
 */
export class AdminVenues extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, isLoading: true, saving: false, venues: [], seasons: [],
			selectedVenueId: null, charts: [], selectedChartId: null, areas: [],
			dialog: null, edit: null, seatRows: [], seatsBusy: false, selectedSeats: []
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const status = await safeJson(VenueService.status());
			if (status?.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const [venues, seasons] = await Promise.all([
				(await VenueService.getAll()).json(),
				(await SeasonService.getAll()).json()
			]);
			const list = Array.isArray(venues) ? venues : [];
			this.setState({
				venues: list,
				seasons: Array.isArray(seasons) ? seasons : [],
				isLoading: false
			});
			if (list.length) this.selectVenue(list[0].id);
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	async selectVenue(venueId: number) {
		this.setState({ selectedVenueId: venueId, charts: [], selectedChartId: null, areas: [] });
		try {
			const charts = await safeJson(VenueService.getCharts(venueId));
			const list = Array.isArray(charts) ? charts : [];
			this.setState({ charts: list });
			if (list.length) this.selectChart(list[0].id);
		} catch { /* leave empty */ }
	}

	async selectChart(chartId: number) {
		this.setState({ selectedChartId: chartId, areas: [] });
		try {
			const areas = await safeJson(VenueService.getAreas(chartId));
			this.setState({ areas: Array.isArray(areas) ? areas : [] });
		} catch { /* leave empty */ }
	}

	open(dialog: State["dialog"], edit: any = {}) {
		this.setState({ dialog, edit, selectedSeats: [] });
		if (dialog === "editSeats" && edit.blockId) this.loadSeats(edit.blockId);
	}

	/**
	 * The seats in a block, so the odd ones can be edited.
	 *
	 * The generator lays out a perfect grid; a real stand has a camera gantry
	 * in the middle of row F and two wheelchair spaces at the end of A. Until
	 * now the endpoints for that existed and nothing called them, so the hint
	 * telling clubs to "edit the odd ones afterwards" pointed at nothing.
	 */
	async loadSeats(blockId: number) {
		this.setState({ seatsBusy: true });
		try {
			const res = await VenueService.getSeats(blockId);
			const body = res.ok ? await res.json() : null;
			this.setState({ seatRows: body?.rows || body || [], seatsBusy: false });
		} catch {
			this.setState({ seatRows: [], seatsBusy: false });
		}
	}

	toggleSeat(seatId: number) {
		this.setState(({ selectedSeats }) => ({
			selectedSeats: selectedSeats.includes(seatId)
				? selectedSeats.filter((s) => s !== seatId)
				: [...selectedSeats, seatId]
		}));
	}

	/** Select or clear a whole row — blocking a row one seat at a time is nobody's afternoon. */
	toggleRow(seats: any[]) {
		const ids = seats.map((s: any) => s.id ?? s.seatId);
		const allOn = ids.every((id: number) => this.state.selectedSeats.includes(id));
		this.setState(({ selectedSeats }) => ({
			selectedSeats: allOn
				? selectedSeats.filter((id) => !ids.includes(id))
				: Array.from(new Set([...selectedSeats, ...ids]))
		}));
	}

	async applyToSelection(patch: { isBlocked?: boolean; isAccessible?: boolean }) {
		const { selectedSeats, edit } = this.state;
		if (!selectedSeats.length) return;

		this.setState({ seatsBusy: true });
		try {
			// One call per seat: the endpoint takes a single seat, and a bulk
			// version isn't worth adding for a handful at a time.
			await Promise.all(selectedSeats.map((id) => VenueService.updateSeat(id, patch)));
			this.toast.show({
				closable: false,
				detail: `${selectedSeats.length} seat${selectedSeats.length === 1 ? "" : "s"} updated.`,
				life: 3000, severity: "success", summary: "Seats"
			});
			this.setState({ selectedSeats: [] });
			if (edit?.blockId) await this.loadSeats(edit.blockId);
		} catch {
			this.toast.show({ closable: false, detail: "Couldn't update those seats.", life: 4000, severity: "error", summary: "Seats" });
		} finally {
			this.setState({ seatsBusy: false });
		}
	}

	async save() {
		const { dialog, edit, selectedVenueId, selectedChartId } = this.state;
		this.setState({ saving: true });
		let res: Response | null = null;

		try {
			if (dialog === "venue") {
				res = edit.id
					? await VenueService.update(edit.id, edit)
					: await VenueService.add(edit);
			} else if (dialog === "chart" && selectedVenueId) {
				res = edit.cloneFrom
					? await VenueService.cloneChart(edit.cloneFrom, { name: edit.name, seasonId: edit.seasonId })
					: await VenueService.addChart(selectedVenueId, edit);
			} else if (dialog === "area" && selectedChartId) {
				res = edit.id
					? await VenueService.updateArea(edit.id, edit)
					: await VenueService.addArea(selectedChartId, edit);
			} else if (dialog === "block" && edit.areaId) {
				res = await VenueService.addBlock(edit.areaId, { name: edit.name, sortOrder: edit.sortOrder || 0 });
			} else if (dialog === "seats" && edit.blockId) {
				res = await VenueService.generateSeats(edit.blockId, {
					replace: !!edit.replace,
					rows: rowLabels(Number(edit.rowCount) || 0),
					seatsPerRow: Number(edit.seatsPerRow) || 0,
					startNumber: Number(edit.startNumber) || 1
				});
			}
		} catch { /* handled below */ }

		this.setState({ saving: false });

		if (!res || !res.ok) {
			const message = res ? await res.text() : "";
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't save" });
			return;
		}

		if (dialog === "seats") {
			const body = await res.json();
			this.toast.show({ closable: false, detail: `${body.created} seats created.`, life: 3000, severity: "success", summary: "Seats generated" });
		}

		this.setState({ dialog: null, edit: null });

		// Refresh from wherever the change landed.
		if (dialog === "venue") this.load();
		else if (dialog === "chart" && selectedVenueId) this.selectVenue(selectedVenueId);
		else if (selectedChartId) this.selectChart(selectedChartId);
	}

	async removeArea(area: any) {
		if (!window.confirm(`Remove "${area.name}"? Tickets already sold in it keep working.`)) return;
		await VenueService.removeArea(area.id);
		if (this.state.selectedChartId) this.selectChart(this.state.selectedChartId);
	}

	async removeBlock(block: any) {
		if (!window.confirm(`Remove "${block.name}" and its ${block.seats} seats?`)) return;
		await VenueService.removeBlock(block.id);
		if (this.state.selectedChartId) this.selectChart(this.state.selectedChartId);
	}

	renderDialog() {
		const { dialog, edit, seasons, charts } = this.state;
		if (!dialog || !edit) return null;

		const set = (patch: any) => this.setState({ edit: { ...edit, ...patch } });

		const titles: any = {
			area: edit.id ? "Edit area" : "New area",
			block: "New block",
			chart: edit.cloneFrom ? "Copy seating chart" : "New seating chart",
			editSeats: `Seats — ${edit.blockName || "block"}`,
			seats: "Generate seats",
			venue: edit.id ? "Edit venue" : "New venue"
		};

		// The seat editor applies each change as it's made, so a Save button
		// would suggest the previous ones hadn't been.
		const liveEditing = dialog === "editSeats";

		return (
			<Dialog footer={liveEditing
				? <Button label="Done" onClick={() => this.setState({ dialog: null })} />
				: <>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ dialog: null })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={titles[dialog]} modal onHide={() => this.setState({ dialog: null })} style={{ maxWidth: "95vw", width: liveEditing ? 720 : 480 }} visible>

				{dialog === "venue" && <>
					<label className="vn-label">Name</label>
					<InputText autoFocus onChange={(e) => set({ name: e.target.value })} placeholder="Glanford Park" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />
					<label className="vn-label">Address</label>
					<InputText onChange={(e) => set({ address: e.target.value })} style={{ marginBottom: 14, width: "100%" }} value={edit.address || ""} />
					<label className="vn-label">Postcode</label>
					<InputText onChange={(e) => set({ postcode: e.target.value })} style={{ marginBottom: 16, width: "100%" }} value={edit.postcode || ""} />
					<div className="vn-switch">
						<InputSwitch checked={!!edit.isDefault} onChange={(e) => set({ isDefault: e.value })} />
						<label>Our usual home &mdash; fixtures default to it</label>
					</div>
				</>}

				{dialog === "chart" && <>
					<label className="vn-label">Name</label>
					<InputText autoFocus onChange={(e) => set({ name: e.target.value })} placeholder="2026/2027 - Glanford Park" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />
					<label className="vn-label">Season</label>
					<select className="vn-select" onChange={(e) => set({ seasonId: e.target.value ? Number(e.target.value) : null })} value={edit.seasonId ?? ""}>
						<option value="">Not tied to a season</option>
						{seasons.map((s) => <option key={`cs-${s.id}`} value={s.id}>{s.name}</option>)}
					</select>
					{!edit.cloneFrom && charts.length > 0 &&
						<>
							<label className="vn-label" style={{ marginTop: 16 }}>Copy from</label>
							<select className="vn-select" onChange={(e) => set({ cloneFrom: e.target.value ? Number(e.target.value) : null })} value={edit.cloneFrom ?? ""}>
								<option value="">Start empty</option>
								{charts.map((c) => <option key={`cf-${c.id}`} value={c.id}>{c.name}</option>)}
							</select>
							<p className="vn-hint">Copying brings every area, block and seat across. Quicker than rebuilding a ground you already described.</p>
						</>}
				</>}

				{dialog === "area" && <>
					<label className="vn-label">Name</label>
					<InputText autoFocus onChange={(e) => set({ name: e.target.value })} placeholder="The Threadgold Stand" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />
					<label className="vn-label">Turnstiles</label>
					<InputText onChange={(e) => set({ turnstiles: e.target.value })} placeholder="Turnstiles 1A, B, C" style={{ marginBottom: 6, width: "100%" }} value={edit.turnstiles || ""} />
					<p className="vn-hint">Printed on the ticket, so write it the way a steward says it.</p>
					<div className="vn-switch" style={{ marginTop: 12 }}>
						<InputSwitch checked={!!edit.isUnallocated} onChange={(e) => set({ isUnallocated: e.value })} />
						<label>Standing / unallocated</label>
					</div>
					{edit.isUnallocated &&
						<>
							<label className="vn-label" style={{ marginTop: 14 }}>Capacity</label>
							<InputNumber onValueChange={(e) => set({ capacity: e.value })} style={{ width: "100%" }} value={edit.capacity} />
							<p className="vn-hint">Seated areas take their capacity from the seat count, so this only applies to terracing.</p>
						</>}
					<div className="vn-switch" style={{ marginTop: 14 }}>
						<InputSwitch checked={!!edit.isAccessible} onChange={(e) => set({ isAccessible: e.value })} />
						<label>Accessible</label>
					</div>
				</>}

				{dialog === "block" && <>
					<label className="vn-label">Name</label>
					<InputText autoFocus onChange={(e) => set({ name: e.target.value })} placeholder="Threadgold 32-59" style={{ width: "100%" }} value={edit.name || ""} />
				</>}

				{dialog === "editSeats" && <>
					<p className="vn-hint" style={{ marginTop: 0 }}>
						Tap seats to select them, or a row letter for the whole row. Blocked seats stay in the
						plan and can&rsquo;t be sold &mdash; a camera gantry, a segregation line, a broken seat.
					</p>

					{this.state.seatsBusy && <p className="vn-hint">Working&hellip;</p>}

					{!this.state.seatsBusy && !this.state.seatRows.length &&
						<p className="vn-hint">No seats in this block yet. Generate some first.</p>}

					<div className="vn-seatmap">
						{this.state.seatRows.map((row: any) => {
							const seats = row.seats || [];
							return (
								<div className="vn-seatrow" key={row.rowLabel}>
									<button className="vn-rowlabel" onClick={() => this.toggleRow(seats)} type="button">
										{row.rowLabel}
									</button>
									{seats.map((seat: any) => {
										const seatId = seat.id ?? seat.seatId;
										const picked = this.state.selectedSeats.includes(seatId);
										return (
											<button
												className={`vn-seat${picked ? " picked" : ""}${seat.isBlocked ? " blocked" : ""}${seat.isAccessible ? " accessible" : ""}`}
												key={seatId}
												onClick={() => this.toggleSeat(seatId)}
												title={`${row.rowLabel}${seat.seatNumber}${seat.isBlocked ? " — blocked" : ""}${seat.isAccessible ? " — accessible" : ""}`}
												type="button"
											>
												{seat.seatNumber}
											</button>
										);
									})}
								</div>
							);
						})}
					</div>

					<div className="vn-seatkey">
						<span><i className="blocked" /> Blocked</span>
						<span><i className="accessible" /> Accessible</span>
					</div>

					{this.state.selectedSeats.length > 0 &&
						<div className="vn-seatactions">
							<strong>{this.state.selectedSeats.length} selected</strong>
							<Button className="p-button-sm" disabled={this.state.seatsBusy} label="Block" onClick={() => this.applyToSelection({ isBlocked: true })} />
							<Button className="p-button-sm p-button-outlined" disabled={this.state.seatsBusy} label="Unblock" onClick={() => this.applyToSelection({ isBlocked: false })} />
							<Button className="p-button-sm p-button-outlined" disabled={this.state.seatsBusy} label="Accessible" onClick={() => this.applyToSelection({ isAccessible: true })} />
							<Button className="p-button-sm p-button-outlined" disabled={this.state.seatsBusy} label="Not accessible" onClick={() => this.applyToSelection({ isAccessible: false })} />
						</div>}
				</>}

				{dialog === "seats" && <>
					<p className="vn-hint" style={{ marginTop: 0 }}>
						Rows are labelled A, B, C&hellip; and numbered from your starting number. Edit the odd ones afterwards.
					</p>
					<label className="vn-label">How many rows</label>
					<InputNumber onValueChange={(e) => set({ rowCount: e.value })} style={{ marginBottom: 14, width: "100%" }} value={edit.rowCount} />
					<label className="vn-label">Seats per row</label>
					<InputNumber onValueChange={(e) => set({ seatsPerRow: e.value })} style={{ marginBottom: 14, width: "100%" }} value={edit.seatsPerRow} />
					<label className="vn-label">First seat number</label>
					<InputNumber onValueChange={(e) => set({ startNumber: e.value })} style={{ marginBottom: 16, width: "100%" }} value={edit.startNumber ?? 1} />
					<div className="vn-switch">
						<InputSwitch checked={!!edit.replace} onChange={(e) => set({ replace: e.value })} />
						<label>Replace the seats already in this block</label>
					</div>
					{(edit.rowCount > 0 && edit.seatsPerRow > 0) &&
						<p className="vn-total">
							{edit.rowCount * edit.seatsPerRow} seats, rows {rowLabels(edit.rowCount)[0]}&ndash;{rowLabels(edit.rowCount).slice(-1)[0]}
						</p>}
				</>}
			</Dialog>
		);
	}

	render() {
		const { available, isLoading, venues, charts, areas, selectedVenueId, selectedChartId } = this.state;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Venues</h2>
					<div className="vn-empty">
						<FontAwesomeIcon icon={faLocationDot} />
						<h3>Venues aren&rsquo;t switched on yet</h3>
						<p>The venue tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Venues &amp; seating</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 22 }}>
					Where you play, and how it&rsquo;s laid out. Ticket prices, allocations and the gate all hang off these areas.
				</p>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && <div className="vn-split">
					<div className="vn-col">
						<div className="vn-col-head"><span>Venues</span><Button className="p-button-text" icon="pi pi-plus" onClick={() => this.open("venue", {})} /></div>
						{venues.length === 0 && <p className="vn-none">None yet.</p>}
						{venues.map((v) => (
							<button className={`vn-item${selectedVenueId === v.id ? " active" : ""}`} key={`v-${v.id}`} onClick={() => this.selectVenue(v.id)} type="button">
								{v.name}{v.isDefault && <small>Home</small>}
							</button>
						))}
					</div>

					<div className="vn-col">
						<div className="vn-col-head">
							<span>Seating charts</span>
							{selectedVenueId && <Button className="p-button-text" icon="pi pi-plus" onClick={() => this.open("chart", {})} />}
						</div>
						{!selectedVenueId && <p className="vn-none">Pick a venue.</p>}
						{selectedVenueId && charts.length === 0 && <p className="vn-none">No charts yet.</p>}
						{charts.map((c) => (
							<button className={`vn-item${selectedChartId === c.id ? " active" : ""}`} key={`c-${c.id}`} onClick={() => this.selectChart(c.id)} type="button">
								{c.name}<small>{c.areas} area{c.areas === 1 ? "" : "s"}</small>
							</button>
						))}
					</div>

					<div className="vn-areas">
						<div className="vn-col-head">
							<span>Areas</span>
							{selectedChartId && <Button icon="pi pi-plus" label="Add area" onClick={() => this.open("area", {})} />}
						</div>

						{!selectedChartId && <p className="vn-none">Pick a seating chart.</p>}
						{selectedChartId && areas.length === 0 &&
							<div className="vn-empty">
								<FontAwesomeIcon icon={faChairOffice} />
								<h3>No areas yet</h3>
								<p>Add your stands and terraces. Each one carries its turnstiles and either a seat layout or a capacity.</p>
							</div>}

						{areas.map((area) => (
							<div className="vn-area" key={`a-${area.id}`}>
								<div className="vn-area-head">
									<div>
										<h4>{area.name}</h4>
										{area.turnstiles && <span className="vn-turnstiles">{area.turnstiles}</span>}
									</div>
									<div className="vn-area-actions">
										<span className="vn-capacity">{area.capacity || 0}{area.isUnallocated ? " standing" : " seats"}</span>
										{area.isAccessible && <span className="vn-tag">ACCESSIBLE</span>}
										<Button className="p-button-text" label="Edit" onClick={() => this.open("area", { ...area })} style={{ padding: "3px 7px" }} />
										<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeArea(area)} style={{ padding: "3px 7px" }} />
									</div>
								</div>

								{!area.isUnallocated && <>
									<div className="vn-blocks">
										{(area.blocks || []).map((block: any) => (
											<div className="vn-block" key={`b-${block.id}`}>
												<span>{block.name}<small>{block.seats} seats</small></span>
												<span>
													<Button className="p-button-text" label="Seats" onClick={() => this.open("seats", { blockId: block.id, startNumber: 1 })} style={{ padding: "2px 6px" }} />
													<Button className="p-button-text" label="Edit" onClick={() => this.open("editSeats", { blockId: block.id, blockName: block.name })} style={{ padding: "2px 6px" }} />
													<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeBlock(block)} style={{ padding: "2px 6px" }} />
												</span>
											</div>
										))}
									</div>
									<Button className="p-button-text" icon="pi pi-plus" label="Add block" onClick={() => this.open("block", { areaId: area.id })} style={{ padding: "4px 8px" }} />
								</>}
							</div>
						))}
					</div>
				</div>}

				{this.renderDialog()}
			</div>
		);
	}
}
